/**
 * What the signed-in account has to do, grouped by why.
 *
 * Every item was a card inside a card inside a card, three nested surfaces
 * deep, and the one thing worth acting on sat at the bottom of each.
 *
 * @module components/overview/AttentionQueue
 */

import React from "react";

import { QueueStatusBadge } from "@/components/shared/QueueStatusBadge";
import type { BackendDashboardSection } from "@/components/shared/workflow-queue-logic";
import {
  Alert,
  Badge,
  EmptyState,
  Heading,
  Icon,
  ListRow,
  MetaBadgeRow,
  MutedText,
  Panel,
} from "@/components/ui";
import { ListBones } from "@/components/ui/SkeletonParts";

export interface AttentionQueueProps {
  sections: readonly BackendDashboardSection[];
  error?: Error | null;
  /** The read is in flight: a skeleton stands in for the queue, and the empty state stays away. */
  loading?: boolean;
  /** Absent for an account that may not open a programme. */
  onOpen?: (programmeId: string, moduleId?: string) => void;
}

/**
 * @example
 * ```tsx
 * <AttentionQueue sections={getBackendDashboardSections(session, programmes, modules)} />
 * ```
 */
/**
 * One labelled fact on a queue row's meta line.
 *
 * The icon is decorative, so the label is what tells a screen reader which
 * fact this is; without it a row of four bare values reads as one run.
 */
function MetaFact({
  icon,
  label,
  truncate = false,
  children,
}: {
  icon: string;
  label: string;
  truncate?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <span className={`inline-flex items-center gap-1 ${truncate ? "min-w-0" : ""}`.trim()}>
      <Icon name={icon} aria-hidden className="shrink-0" />
      <span className="sr-only">{label}: </span>
      <span className={truncate ? "truncate" : undefined}>{children}</span>
    </span>
  );
}

/** A section's header costs about two rows of height. */
const HEADER_WEIGHT = 2;

/**
 * Deals sections into two columns, each to the shorter one so far, in order.
 * A grid row is as tall as its tallest section, so one long queue left the
 * short ones beside and below it in empty space.
 */
function balanceColumns(sections: readonly BackendDashboardSection[]): BackendDashboardSection[][] {
  const columns: BackendDashboardSection[][] = [[], []];
  const heights = [0, 0];
  sections.forEach((section) => {
    const target = heights[0] <= heights[1] ? 0 : 1;
    columns[target].push(section);
    heights[target] += section.programmes.length + HEADER_WEIGHT;
  });
  return columns.filter((column) => column.length > 0);
}

export function AttentionQueue({
  sections,
  error = null,
  loading = false,
  onOpen = undefined,
}: AttentionQueueProps): React.ReactElement {
  const total = sections.reduce((count, section) => count + section.programmes.length, 0);
  const showEmpty = !loading && !error && total === 0;
  const columns = balanceColumns(sections.filter((section) => section.programmes.length > 0));

  return (
    <Panel title="Needs your attention" icon="warning-circle" data-testid="role-dashboard">
      {error ? <Alert tone="warning">{error.message}</Alert> : null}

      {/* A read that failed counted nothing, and saying so as "nothing needs
          your attention" is the one wrong answer. */}
      {showEmpty ? (
        <EmptyState
          icon="check-circle"
          title="Nothing needs your attention"
          description="Work assigned to you appears here as it arrives."
          data-testid="attention-queue-empty"
        />
      ) : null}
      {loading ? <ListBones rows={3} boxed label="Loading your queue" /> : null}
      {showEmpty || loading ? null : (
        <div
          className={`grid items-start gap-6 ${columns.length > 1 ? "xl:grid-cols-2" : ""}`.trim()}
          data-testid="attention-sections"
        >
          {columns.map((column) => (
            <div key={column[0].title} className="flex flex-col gap-6">
              {column.map((section) => {
                // One role on every row is said once, on the section.
                const roles = new Set(section.programmes.map((item) => item.yourRole ?? ""));
                const sharedRole = roles.size === 1 ? [...roles][0] : "";
                return (
                  <section key={section.title} aria-label={section.title}>
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <Heading level={3} size="md">
                        {section.title}
                      </Heading>
                      <span className="flex flex-wrap items-center gap-2">
                        {sharedRole ? (
                          <Badge tone="neutral" size="sm" variant="soft">
                            <Icon name="identification-badge" aria-hidden />
                            <span className="sr-only">Your role: </span>
                            {sharedRole}
                          </Badge>
                        ) : null}
                        <Badge tone="neutral" size="sm">
                          {section.programmes.length}{" "}
                          {section.programmes.length === 1 ? "item" : "items"}
                        </Badge>
                      </span>
                    </div>
                    <MutedText size="caption" as="p" className="mt-1">
                      {section.summary}
                    </MutedText>

                    <ul className="mt-3 flex flex-col gap-2">
                      {section.programmes.map((item) => (
                        <li key={item.id}>
                          <ListRow
                            data-testid={`attention-item-${item.id}`}
                            action={
                              onOpen
                                ? {
                                    label: `${section.openLabel} for ${item.title}`,
                                    onClick: () => {
                                      onOpen(item.programmeId, item.moduleId);
                                    },
                                    "data-testid": `work-queue-open-${item.id}`,
                                  }
                                : undefined
                            }
                          >
                            <span className="block font-semibold">{item.title}</span>
                            {/* The state heads the meta line rather than sharing
                            the title's. Beside the title it took width the
                            title needed — three lines for one name on a phone
                            — and it landed somewhere different on every card,
                            because where it sat depended on how long that
                            card's title happened to be. Each fact gets its own
                            mark, so the line reads as facts rather than one
                            run-on caption. */}
                            <MetaBadgeRow className="mt-1.5 gap-x-3">
                              <QueueStatusBadge status={item.status} kind={item.kind} size="sm" />
                              {/* Each fact names itself for a reader who cannot
                              see the mark. Without it the row announced as
                              four values in a run — "In Development BSc
                              Computing Alice Module" — and the owner in
                              particular read as the reader's own role. */}
                              {item.yourRole && !sharedRole ? (
                                <MetaFact icon="identification-badge" label="Your role">
                                  {item.yourRole}
                                </MetaFact>
                              ) : null}
                              {item.programmeTitle ? (
                                <MetaFact icon="graduation-cap" label="Programme" truncate>
                                  {item.programmeTitle}
                                </MetaFact>
                              ) : null}
                              {item.owner ? (
                                <MetaFact icon="user" label="Builder" truncate>
                                  {item.owner}
                                </MetaFact>
                              ) : null}
                              {item.kind ? (
                                <MetaFact
                                  icon={item.kind === "Module" ? "squares-four" : "graduation-cap"}
                                  label="Kind"
                                >
                                  {item.kind}
                                </MetaFact>
                              ) : null}
                              <MetaFact icon="clock-counter-clockwise" label="Updated">
                                {item.updated}
                              </MetaFact>
                            </MetaBadgeRow>
                            {item.attentionReason ? (
                              <MutedText size="caption" as="p" className="mt-2">
                                {item.attentionReason}
                              </MutedText>
                            ) : null}
                          </ListRow>
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
